import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { LoadingTimeout } from '../ui/LoadingTimeout';

interface ProtectedRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean;
  boardOnly?: boolean;
}

export function ProtectedRoute({ children, adminOnly = false, boardOnly = false }: ProtectedRouteProps) {
  const { isAuthenticated, user, loading, adminResolved } = useAuth();
  const { currentResidence, residences, membershipResolved } = useUser();
  const location = useLocation();

  // For admin/board ruter må vi vente på rolle-data
  const needsRoleCheck = adminOnly || boardOnly;
  const waitingForRoleData = needsRoleCheck && (!membershipResolved || !adminResolved);
  
  // Vis spinner KUN hvis:
  // 1. Auth fortsatt laster OG vi ikke har optimistisk session, ELLER
  // 2. Vi trenger rolle-sjekk og venter på data
  if ((loading && !isAuthenticated) || waitingForRoleData) {
    console.log('[ProtectedRoute spinner]', { 
      loading, 
      isAuthenticated,
      needsRoleCheck,
      waitingForRoleData,
      path: location.pathname 
    });
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <LoadingTimeout 
          isLoading={true} 
          variant="light" 
          loadingText={needsRoleCheck ? "Sjekker tilgang..." : "Laster..."} 
        />
      </div>
    );
  }

  // Hvis brukeren ikke er autentisert, redirect til login
  if (!isAuthenticated) {
    console.log('[ProtectedRoute redirect → /login]', { path: location.pathname });
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If admin access is required but user is not admin
  if (adminOnly && !user?.isAdmin) {
    console.log('[ProtectedRoute block adminOnly]', { path: location.pathname });
    return <Navigate to="/" replace />;
  }

  // If board access is required but user is not a board member in ANY affiliation
  // Admin har også tilgang til board-ruter
  if (boardOnly) {
    const isBoard = user?.isAdmin || (currentResidence?.role === 'board') || (residences || []).some(r => r.role === 'board');
    if (!isBoard) {
      console.log('[ProtectedRoute block boardOnly]', { path: location.pathname, role: currentResidence?.role });
      return <Navigate to="/" replace />;
    }
  }

  // If all checks pass, render the protected content
  return <>{children}</>;
}